import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ROUTERS, ROUTERS } from '../../data';
import { useRequest } from '../../hooks';
import { removeAuthToken, setAuthToken, baseRequest, getAuthToken } from '../../services';
import { authContext } from './authContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { request, authRequest } = useRequest();
  const isLogoutSended: MutableRefObject<boolean> = useRef(false);

  const signIn = useCallback(
    async (formValues: { email: string; password: string }) => {
      try {
        const { data } = await request(
          { method: 'post', url: API_ROUTERS.LOGIN, data: formValues },
          { hideErrorMessage: true },
        );
        if (data?.token) {
          setAuthToken(data?.token);
          navigate(ROUTERS.USER.PROFILE);
        }
      } catch (e: any) {
        throw e;
      }
    },
    [navigate, request],
  );

  const signOut = useCallback(
    async (options?: { nextRoute?: string } | any) => {
      const openAfterLogoutScreen = () => {
        navigate(options?.nextRoute || ROUTERS.INIT, { replace: true });
      };

      if (getAuthToken() && !isLogoutSended.current) {
        isLogoutSended.current = true;
        try {
          await authRequest({ method: 'get', url: API_ROUTERS.LOGOUT }, { hideErrorMessage: true });
        } finally {
          removeAuthToken();
          openAfterLogoutScreen();
          isLogoutSended.current = false;
        }
      } else {
        openAfterLogoutScreen();
      }
    },
    [navigate, authRequest],
  );

  useEffect(() => {
    // Http interceptor
    baseRequest.interceptors.response.use(
      (res) => res,
      (err) => {
        if (
          err?.response?.status === 401 &&
          err?.config?.url !== API_ROUTERS.LOGOUT // logout can return 401 if token invalid
        ) {
          signOut();
        }
        return Promise.reject(err);
      },
    );

    // if token was removed in another tab
    const storageListener = () => {
      if (!getAuthToken()) {
        signOut();
      }
    };
    window.addEventListener('storage', storageListener);

    return () => {
      window.removeEventListener('storage', storageListener);
    };
  }, [signOut]);

  const value = useMemo(() => ({ signIn, signOut }), [signOut, signIn]);

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};
